import { Col, Row, notification } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';

import { Command, CommandBar } from '../../../components/CommandBar';
import { GdModal } from '../../../components/GdModal';
import { GdTabs } from '../../../components/GdTabs';
import { PageTitle } from '../../../components/PageTitle';
import { useRootStore } from '../../../context/storeContext';
import { useToggle } from '../../../hooks/useToggle';
import {
  BasePageContainer,
  CardsDetailsContainer,
  CommandsContainer,
} from '../../Common/styles';
import { useSubscriptionsQuery } from '../hooks/useOrgSettingsMeta';
import { OrgActivity } from './ActivityLog/OrgActivity';
import { OrgManageUsers } from './Management/OrgManageUsers';
import { OrgSubscriptionSettings } from './Management/OrgSubscriptionSettings';
import { OrgMetaDataCard } from './OrgMetaDataCard';
import { TemplateList } from './TemplateList';

const OrgDetailsContainer = styled(BasePageContainer)``;
export const OrgDetails = observer(() => {
  const { t } = useTranslation();
  const { orgId } = useParams<{ orgId: string }>();
  const rootStore = useRootStore();
  const { orgListStore } = rootStore;
  const { selectedOrg, subscriptionOptions } = orgListStore;

  useSubscriptionsQuery();

  useEffect(() => {
    const getOrgData = async () => {
      await orgListStore.getById(orgId);
    };
    getOrgData();

    return () => {
      orgListStore.clearOrg();
    };
  }, [orgId, orgListStore]);

  const [isModalShown, toggleModal] = useToggle(false);
  const [isGuidoLibraryModalShown, toggleGuidoLibraryModal] = useToggle(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [isAssigningTemplates, setIsAssigningTemplates] =
    useState<boolean>(false);

  const commands: Command[] = [
    {
      title: t('admOrgs:Commands:addGuidoFromLibraryLbl'),
      handler: toggleGuidoLibraryModal,
    },
    {
      title: t('admOrgs:Commands:manageOrgLbl'),
      handler: toggleModal,
    },
  ];

  const resetModalPadding = useCallback(() => {
    return css`
      padding: 20px 0 0 0;
    `;
  }, []);

  const modalTitle = useMemo(() => {
    return <PageTitle isSuper title={t('admOrgs:ManageOrgModal:mainText')} />;
  }, [t]);

  const onModalClose = useCallback(() => {
    toggleModal();

    if (selectedOrg.isSettingsDirty) {
      selectedOrg.resetSettingsToDefaults!();
    }
  }, [selectedOrg, toggleModal]);

  const handleTemplateSelectionChange = useCallback((selectedIds: string[]) => {
    setSelectedTemplateIds(selectedIds);
  }, []);

  const handleAssignTemplates = useCallback(async () => {
    // Set loading state to true when starting the operation
    setIsAssigningTemplates(true);

    try {
      // Call the API to assign templates to the current organization
      const result = await rootStore.transportLayer.assignTemplates(
        orgId,
        selectedTemplateIds
      );

      if (result.succeeded) {
        // Show success notification
        notification.success({
          message: 'Templates Assigned Successfully',
          description: result.message,
          placement: 'bottomRight',
          duration: 3,
        });

        // Clear selections
        setSelectedTemplateIds([]);

        // Close modal on success
        toggleGuidoLibraryModal();
      } else {
        // Show error notification
        notification.error({
          message: 'Failed to Assign Templates',
          description:
            result.message || 'An error occurred while assigning templates',
          placement: 'bottomRight',
          duration: 3,
        });
      }
    } catch (error) {
      console.error('Error assigning templates:', error);

      // Show error notification for unexpected errors
      notification.error({
        message: 'Error',
        description:
          'An unexpected error occurred while assigning templates. Please try again.',
        placement: 'bottomRight',
        duration: 3,
      });
    } finally {
      // Always reset loading state
      setIsAssigningTemplates(false);
    }
  }, [
    orgId,
    selectedTemplateIds,
    rootStore.transportLayer,
    toggleGuidoLibraryModal,
  ]);

  return (
    <OrgDetailsContainer>
      <Row>
        <CommandsContainer>
          <PageTitle isSuper title={t('admOrgs:Label:mainTitle')} />
          <CommandBar commands={commands} />
        </CommandsContainer>
      </Row>
      <CardsDetailsContainer>
        <Row gutter={24}>
          <Col span={7}>
            <OrgMetaDataCard />
          </Col>
          <Col span={17}>
            <OrgActivity />
          </Col>
        </Row>
      </CardsDetailsContainer>
      <GdModal
        isVisible={isModalShown}
        isClosable
        onCancel={onModalClose}
        useCss={resetModalPadding}
        title={modalTitle}
        width={600}
      >
        <GdTabs
          items={[
            {
              title: t('admOrgs:ManageOrgModal:Tabs:settingsTabName'),
              children: (
                <OrgSubscriptionSettings subOptions={subscriptionOptions} />
              ),
            },
            {
              title: t('admOrgs:ManageOrgModal:Tabs:usersTabName'),
              children: <OrgManageUsers />,
            },
          ]}
        />
      </GdModal>
      <GdModal
        isVisible={isGuidoLibraryModalShown}
        isClosable
        onCancel={toggleGuidoLibraryModal}
        onCloseModal={toggleGuidoLibraryModal}
        cancelButtonText={t('admOrgs:GuidoLibraryModal:cancelBtn')}
        onOkHandler={handleAssignTemplates}
        okButtonText={t('admOrgs:GuidoLibraryModal:assignBtn')}
        disableButtons={
          selectedTemplateIds.length === 0 || isAssigningTemplates
        }
        loading={isAssigningTemplates}
        title={t('admOrgs:GuidoLibraryModal:mainText')}
        width={800}
      >
        <TemplateList
          onSelectionChange={handleTemplateSelectionChange}
          restClient={rootStore.ApiClient}
        />
      </GdModal>
    </OrgDetailsContainer>
  );
});
